/**
 * MQTT PTT控制服务
 * 使用MQTT协议控制PTT功能
 * 
 * 功能流程：
 * 1. 单击PTT开启：通过MQTT发送PTT开启命令 -> 设备接收命令 -> 开始音频传输
 * 2. 单击PTT关闭：通过MQTT发送PTT关闭命令 -> 设备接收命令 -> 停止音频传输
 */

import { PttCommand, PttResponse, PttError, PttErrorType, PTT_ERROR_MESSAGES } from '../types/ptt';
import { useUnifiedStore } from '../stores/unifiedStore';
import { PttStateValidator, StateSnapshot } from '../utils/stateValidator';
import { mqttService } from './mqttService';
import { authService } from './authService';

/**
 * 错误处理工具函数
 */
class MqttPttErrorHandler {
  /**
   * 创建错误对象
   */
  static createError(type: PttErrorType, originalError?: any): PttError {
    return {
      code: type,
      message: PTT_ERROR_MESSAGES[type] || PTT_ERROR_MESSAGES[PttErrorType.UNKNOWN_ERROR],
      details: originalError,
      timestamp: Date.now()
    };
  }

  /**
   * 根据原始错误判断错误类型
   */
  static classifyError(error: any): PttErrorType {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return PttErrorType.NETWORK_ERROR;
    }
    
    if (error.message?.includes('设备未连接') || error.message?.includes('设备未找到')) {
      return PttErrorType.DEVICE_NOT_FOUND;
    }
    
    if (error.message?.includes('设备离线') || error.message?.includes('连接失败')) {
      return PttErrorType.DEVICE_OFFLINE;
    }
    
    if (error.message?.includes('权限') || error.message?.includes('认证')) {
      return PttErrorType.PERMISSION_DENIED;
    }
    
    if (error.message?.includes('MQTT') || error.message?.includes('mqtt')) {
      return PttErrorType.NETWORK_ERROR;
    }
    
    return PttErrorType.UNKNOWN_ERROR;
  }
}

/**
 * MQTT PTT服务类
 */
class MqttPttService {
  private stateValidator: PttStateValidator;
  private isInitialized = false;

  constructor() {
    this.stateValidator = new PttStateValidator();
  }

  /**
   * 初始化服务
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      if (!mqttService.isConnected()) {
        await mqttService.connect();
      }

      this.isInitialized = true;
      console.log('MQTT PTT服务初始化成功');
    } catch (error) {
      console.error('MQTT PTT服务初始化失败:', error);
      throw MqttPttErrorHandler.createError(PttErrorType.INITIALIZATION_ERROR, error);
    }
  }

  /**
   * PTT开启
   */
  async pttOn(deviceId: string): Promise<PttResponse> {
    try {
      await this.initialize();

      // 验证设备ID
      if (!deviceId) {
        throw MqttPttErrorHandler.createError(PttErrorType.INVALID_DEVICE_ID);
      }

      // 获取当前状态快照
      const stateSnapshot = this.getCurrentStateSnapshot();
      console.log('PTT开启前状态快照:', stateSnapshot);
      
      // 获取完整的store状态用于验证
      const store = useUnifiedStore.getState();
      console.log('完整store状态:', store);
      
      // 验证状态一致性
      const validationResult = PttStateValidator.validatePttState(store);
      if (!validationResult.isValid) {
        console.error('状态验证失败:', validationResult.errors);
        throw MqttPttErrorHandler.createError(PttErrorType.STATE_INCONSISTENCY, validationResult.errors);
      }

      // 构建PTT开启命令
      const command: PttCommand = {
        deviceId: deviceId,
        command: 'start_session',
        timestamp: Date.now()
      };

      // 检查MQTT服务状态
      const mqttStatus = mqttService.getStatus();
      console.log('MQTT服务状态:', mqttStatus);
      
      if (!mqttStatus.isConnected) {
        throw new Error('MQTT服务未连接，无法发送PTT命令');
      }

      // 通过MQTT发送PTT开启命令
      console.log('开始发送PTT开启命令到设备:', deviceId);
      await mqttService.sendPttCommand(deviceId, 'start');

      console.log('PTT开启命令已通过MQTT发送:', command);

      // 更新本地状态
      this.updateLocalPttState(deviceId, true);

      return {
        success: true,
        message: 'PTT开启成功',
        data: { deviceId, timestamp: Date.now() }
      };

    } catch (error) {
      console.error('PTT开启失败:', error);
      const pttError = MqttPttErrorHandler.createError(
        MqttPttErrorHandler.classifyError(error),
        error
      );
      
      // 更新错误状态
      this.updateLocalPttState(deviceId, false, pttError);
      
      throw pttError;
    }
  }

  /**
   * PTT关闭
   */
  async pttOff(deviceId: string): Promise<PttResponse> {
    try {
      await this.initialize();

      // 验证设备ID
      if (!deviceId) {
        throw MqttPttErrorHandler.createError(PttErrorType.INVALID_DEVICE_ID);
      }

      // 获取当前状态快照
      const stateSnapshot = this.getCurrentStateSnapshot();
      console.log('PTT关闭前状态快照:', stateSnapshot);
      
      // 获取完整的store状态用于验证
      const store = useUnifiedStore.getState();
      console.log('完整store状态:', store);
      
      // 对于PTT关闭操作，跳过严格的状态验证
      // 因为关闭时状态可能已经不一致，这是正常的
      console.log('PTT关闭操作，跳过状态验证');

      // 构建PTT关闭命令
      const command: PttCommand = {
        deviceId: deviceId,
        command: 'stop_session',
        timestamp: Date.now()
      };

      // 检查MQTT服务状态
      const mqttStatus = mqttService.getStatus();
      console.log('MQTT服务状态:', mqttStatus);
      
      if (!mqttStatus.isConnected) {
        throw new Error('MQTT服务未连接，无法发送PTT命令');
      }

      // 通过MQTT发送PTT关闭命令
      console.log('开始发送PTT关闭命令到设备:', deviceId);
      await mqttService.sendPttCommand(deviceId, 'stop');

      console.log('PTT关闭命令已通过MQTT发送:', command);

      // 更新本地状态
      this.updateLocalPttState(deviceId, false);

      return {
        success: true,
        message: 'PTT关闭成功',
        data: { deviceId, timestamp: Date.now() }
      };

    } catch (error) {
      console.error('PTT关闭失败:', error);
      const pttError = MqttPttErrorHandler.createError(
        MqttPttErrorHandler.classifyError(error),
        error
      );
      
      // 更新错误状态
      this.updateLocalPttState(deviceId, false, pttError);
      
      throw pttError;
    }
  }

  /**
   * 获取当前状态快照
   */
  private getCurrentStateSnapshot(): StateSnapshot {
    const store = useUnifiedStore.getState();
    return {
      timestamp: Date.now(),
      pttState: {
        isActive: store.ptt.isActive,
        lastCommand: store.ptt.lastCommand,
        error: store.ptt.error
      },
      deviceState: {
        selectedDeviceId: store.devices.selectedDevice?.getInfo()?.id || null,
        isConnected: store.devices.isConnected
      }
    };
  }

  /**
   * 更新本地PTT状态
   */
  private updateLocalPttState(deviceId: string, isActive: boolean, error?: PttError): void {
    const { actions } = useUnifiedStore.getState();

    if (error) {
      actions.setPttError(error.message);
      actions.setPttActive(false);
    } else {
      if (isActive) {
        actions.setPttActive(true, deviceId);
      } else {
        actions.setPttActive(false);
      }
      actions.setPttError(null);
    }
  }

  /**
   * 获取PTT状态
   */
  getPttStatus(deviceId: string): { isActive: boolean; lastUpdate: number } {
    const store = useUnifiedStore.getState();
    return {
      isActive: store.ptt.isActive,
      lastUpdate: Date.now()
    };
  }

  /**
   * 检查服务是否可用
   */
  isServiceAvailable(): boolean {
    return this.isInitialized && mqttService.isConnected();
  }

  /**
   * 获取服务状态
   */
  getServiceStatus(): { isInitialized: boolean; mqttConnected: boolean } {
    return {
      isInitialized: this.isInitialized,
      mqttConnected: mqttService.isConnected()
    };
  }
}

// 导出单例实例
export const mqttPttService = new MqttPttService();
